import type { BulkPackingPlan } from '@ordo/constants/packing-plan'

import type { ServiceMaterialKind, SpecialDietItem } from '@/api/kitchen-orders'

export type Temperature = 'hot' | 'chilled' | 'ambient'

export type Diet = 'vegetarian' | 'vegan' | 'dairy-free' | 'gluten-free' | 'nut-free' | 'pork-free'

export type Mealtime = 'breakfast' | 'am-snack' | 'lunch' | 'pm-snack' | 'supper'

export type ProductType = 'main' | 'side' | 'beverage' | 'dessert' | 'add-on' | 'salad' | 'baked' | 'shelf'

/** Which kind of special meal service a dry-run shipment belongs to. The prep
 *  and "do not deliver" mechanics are identical — only the label differs. */
export type DryRunServiceType = 'DRY_RUN' | 'TASTING'

export const DRY_RUN_SERVICE_TYPE_LABELS: Record<DryRunServiceType, string> = {
  DRY_RUN: 'Dry run',
  TASTING: 'Tasting',
}

/** Badge/banner label for a dry-run surface. Shipments generated before the
 *  type existed carry no type and read as a dry run. */
export function dryRunServiceTypeLabel(serviceType: DryRunServiceType | null | undefined): string {
  return DRY_RUN_SERVICE_TYPE_LABELS[serviceType ?? 'DRY_RUN']
}

/** Nulls mean "no resolvable amount" — render the name only, never `0 unit`. */
export interface IngredientRef {
  quantity: number | null
  unit: string | null
  /** RECIPE_YIELD (whole-batch amount) vs PER_SERVING — drives scaling. */
  quantityBasis: string | null
  /** Grams per US cup (USDA-derived or hand-set). Null = no trustworthy
   *  density; a weight-authored amount stays a weight. */
  gramsPerCup?: number | null
  name: string
}

export interface Ingredient {
  id: string
  name: string
  quantity: number | null
  unit: string | null
  /** RECIPE_YIELD (whole-batch amount) vs PER_SERVING — drives scaling. */
  quantityBasis: string | null
  /** Grams per US cup (USDA-derived or hand-set). Null = no trustworthy
   *  density; a weight-authored amount stays a weight. */
  gramsPerCup?: number | null
  /** Plan 28 non-linear scaling cap — shown as an advisory beside the scaled
   *  amount, never used to clamp it (the cap is "per batch/roasting pan" and
   *  the pan count is unknown here). */
  maxQuantityValue?: number | null
  maxQuantityUnit?: string | null
  scalingNotes?: string | null
  /** Resolved brand label for processed items — muted sublabel in lists. */
  brand?: string | null
  /** The catalog ingredient this line lists — what a chef picks a brand FOR. */
  ingredientId?: string | null
  /** The option `brand` resolved to; null for variety/generic lines. */
  brandOptionId?: string | null
  /** Which decision produced `brand` — PINNED, KITCHEN, VARIETY, DEFAULT. */
  brandSource?: 'PINNED' | 'KITCHEN' | 'VARIETY' | 'DEFAULT' | null
  /** False when a recipe-line pin makes the brand non-negotiable. */
  brandSelectable?: boolean
  /** How many approved brands this ingredient has. Absent on surfaces that
   *  don't carry catalog ids (the Recipes drawer), which read as plain text. */
  brandOptionCount?: number
}

export interface RecipeStep {
  id: string
  text: string
  /**
   * Ingredients used in this step, with the per-step quantity. Mirrors the
   * production `recipeInstruction.recipeInstructionIngredients[]` shape so
   * each instruction can call out multiple ingredients.
   * Printed recipes show SCALED quantities both here and on the ingredient
   * chips (Yanping Ren, Ops 2026-08-03 — superseding the earlier names-only
   * chip rule): a scaled recipe is unusable without the scaled weights.
   */
  ingredientRefs: IngredientRef[]
}

export interface HACCPPoint {
  id: string
  text: string
  ingredientRef?: IngredientRef
}

export interface Tip {
  id: string
  text: string
}

export interface Recipe {
  id: string
  baseYield: number
  yieldUnit: string
  ingredients: Ingredient[]
  steps: RecipeStep[]
  haccp: HACCPPoint[]
  tips?: Tip[]
}

/**
 * The RECIPE-based portion one serving of a component ships at — the
 * deliverable pick's serving-size option, else the recipe's primary serving
 * (PROD-173). Numeric so the deliverable copy can total it (355 servings x
 * 1/2 cup = 11.1 gal); `Deliverable.servingSize` is the pre-formatted string.
 */
export interface ComponentPortion {
  quantity: number
  unit: string
  /** The serving size's own name, its note ("Package", "1/2 Cup"): "1 each (Package)". Absent when it has none. */
  name?: string
}

export interface Component {
  portionLabel?: string
  disherSize?: string
  id: string
  name: string
  productType: ProductType
  diets: Diet[]
  /** Reviewed allergens by name ("Milk", "Wheat"); absent when none are on file. */
  allergens?: string[]
  /** Every source of the component has reviewed allergen evidence (`KitchenShipmentLineSummary.allergensComplete`). */
  allergensComplete?: boolean
  recipe?: Recipe
  isCondiment?: boolean
  condimentQty?: number
  /** Structured per-serving portion; absent when the recipe declares none. */
  portion?: ComponentPortion
  /** The food's catalog photo (menus asset), for the item chips on the Orders rows. */
  imageUrl?: string
  /** `Component.milkOptionCode` (as on the wire) — set only on milk items. A
   *  package whose items all carry one is milk and leaves the mealtime cards
   *  for the campus milk card (PROD-1502). */
  milkOptionCode?: string
  /**
   * Factor this component's ingredient demand scales by — the shipped
   * serving-size option over the recipe's PRIMARY serving. Absent = 1.
   * Recipe amounts are authored for the primary serving, so any consumer
   * summing ingredients MUST apply this (see `perServingQuantity` callers).
   */
  servingMultiplier?: number
}

/** One part of a deliverable — see `Deliverable.sections`. */
export interface DeliverableSection {
  /** The inner package's SKU name; null for food the outer package holds directly. */
  packaging: string | null
  /** How many of that inner package ride in each meal. */
  perMeal: number
  /** Each food inside, with its recipe serving ("1 Muffin"; null when none). */
  items: Array<{ name: string; serving: string | null }>
}

export interface Deliverable {
  id: string
  name: string
  kind: 'bundle' | 'standalone'
  productType: ProductType
  /** Serving/package count — for bulk lines this is SERVINGS (never trays). */
  qty: number
  unit: string
  bulk: boolean
  /** The food's temperature, as the school serves it. */
  temperature: Temperature
  /**
   * The menu item is delivered cold and heated at the school (the Oct 21 canvas's temp rule): the kitchen's pages say
   * CHILLED, the school copy says `temperature`. Absent until the menu item carries the flag.
   */
  deliverCold?: boolean
  /** Recipe-based serving(s) for the row sublabel. Multi-item packages list
   * every item's serving in title order joined with ' + '
   * ("2 Links + 4 Pancakes"); items with no recipe serving are skipped. */
  servingSize?: string
  packaging?: string
  /**
   * What goes INSIDE `packaging`, part by part (`planDeliverableSections`):
   * the food the outer package holds directly (`packaging: null`), then each
   * inner package — "Kraft Sandwich Bag: Muffin, 1 Muffin", "2x Paper Food Cup:
   * Orange Wedges, 1/2 Cup". Absent when the deliverable has no inner packages
   * holding food; the row then reads as one package.
   */
  sections?: DeliverableSection[]
  /**
   * `Ingredient.packagingSkuKind` of the SKU `packaging` names: INDIVIDUAL
   * means `qty` is a CONTAINER count; BULK (Bulk Tray, Bulk Delivery Box) means
   * `qty` is servings in a shared vessel whose count Ordo does not know. The
   * `bulk` flag alone cannot tell: 309 lines carry a BULK SKU with the flag off.
   * Absent on cached pre-field payloads.
   */
  packagingSkuKind?: string
  /** Reference photo of the packaging `packaging` names — chefs confirm the
   * container by sight rather than decoding its name (PROD-1216). */
  packagingImageUrl?: string
  /** "Condiments: X, Y" line — condiments are excluded from the auto title
   * (shared bundle-title rule) and surface as this muted sublabel instead. */
  condiments?: string
  imageUrl?: string
  diets: Diet[]
  components: Component[]
  /** Mealtime the deliverable belongs to — drives the field-trip bagged copy. */
  mealtime?: Mealtime
  /** Field-trip (bagged) order — per-line flag from materialize time, or the
   * legacy `field-trip` bundle tag. Badge + bagged copy + detail-panel alert. */
  isFieldTrip?: boolean
  /** Derived HOLIDAY TREAT (free with every meal) — packed with the meals but
   * never counted as one. Badge on the row. */
  isHolidayTreat?: boolean
  /** Part of a chef DRY RUN — labeled so a test cook is never mistaken for
   *  live service. */
  isDryRun?: boolean
  /** TASTING vs DRY_RUN — names the badge. Absent means dry run. */
  dryRunServiceType?: DryRunServiceType
  /**
   * How this row's bulk deliverable packs into containers (PROD-1500): one
   * plan per way of packing its package lines' specs give (`bulkPackingSpecKey`:
   * the container and the servings per container), in first-seen order
   * (`shipmentsToCampuses`: the servings of every line packed that way on one
   * campus x delivery day x mealtime x order group, summed, then
   * `planBulkPacking` once; a multi-day row merges each day's plan with
   * `mergeBulkPackingPlans`). Usually one. Never empty when set. Absent when
   * the row's lines have no spec: the row then reads as before ("Standard Hotel
   * Pan → 1 Taco per serving"). A plan spanning two rows renders on the row
   * with more of its servings; the other carries `packedWith` instead.
   */
  packing?: BulkPackingPlan[]
  /** Names of the rows whose containers also hold this row's food ("packed
   * with Corn, Seasoned"): its servings are counted in THEIR packing. */
  packedWith?: string[]
  /**
   * This bulk row carries servings no spec covers (a deliverable, a day or a
   * Section whose container or portions are not sized, or a plan it shared
   * with such a row): it shows no `packing` and no `packedWith`, only the
   * servings (D23, never a wrong count), and a cross-school merge with it
   * shows no containers either. Never set together with `packing` or
   * `packedWith`.
   */
  packingIncomplete?: boolean
  /** SUPPLIES that ship with this package (trays/boats, cutlery, plates —
   * authored per product or per bucket on the template), summed per SKU.
   * Distinct from `packaging`, the container the food is served in. */
  supplies?: SupplyLine[]
  /**
   * Order-group section this row belongs to (PROD-1296) — set when the
   * campus splits its orders (AGE_GROUP bands, rooms, grades…). Rows from
   * different groups never merge; a row from an ungrouped shipment on a
   * grouped campus carries `UNASSIGNED_GROUP_KEY`. Absent on ungrouped
   * campuses and on cross-school aggregates.
   */
  groupKey?: string
  groupLabel?: string
  /**
   * Grade tier this row's portion was resolved for ("K-8", "9-12"). A row
   * merges lines of ONE portion, so the tier is unanimous across them; absent
   * when the lines carry no tier or disagree.
   */
  portionTier?: PortionTier
  /**
   * Render the `portionTier` tag. Set by `markMultiPortionRows` ONLY when the
   * same item appears at more than one portion within one mealtime block —
   * a single-tier campus never shows the tag (Sheila 2026-09-15: "we'll have
   * 2 different SKUs of some meals — how are we labeling that?").
   */
  showPortionTier?: boolean
}

/** Grade tier of a portion — mirrors the wire `KitchenPortionTier`. */
export interface PortionTier {
  code: string
  label: string
}

/** Row-tag copy for a portion tier: "K-8 portion", "9-12 portion". */
export function portionTierTagLabel(tier: PortionTier): string {
  return `${tier.label} portion`
}

/**
 * One order-group section under a mealtime (PROD-1296): the band ("Preschool",
 * "K-8") the kitchen packs separately, with its OWN true meal count. Present
 * on a block only when the campus's shipments carry a group.
 */
export interface MealtimeGroup {
  key: string
  label: string
  /** True meals for this group alone — see `MealtimeBlock.meals`. The
   *  block's `meals` is the sum of its groups'. */
  meals: number
  /** This group's rows; the same objects also appear in `MealtimeBlock.deliverables`. */
  deliverables: Deliverable[]
}

/** `MealtimeGroup.key` of the section holding ungrouped shipments on a grouped campus. */
export const UNASSIGNED_GROUP_KEY = 'unassigned'
export const UNASSIGNED_GROUP_LABEL = 'Unassigned'

export interface MealtimeBlock {
  menuInstructions?: Array<{ menuId: string; menuName: string; delivery: string | null; service: string | null }>
  /** The block's special-diet meals across the campus's shipments, the same meal for the same diet summed. */
  specialDiets?: SpecialDietItem[]

  mealtime: Mealtime
  /** Every row in the block, across groups — the flat list filters, totals
   *  and the shopping list read. */
  deliverables: Deliverable[]
  /**
   * Order-group sections (PROD-1296), in band order with the "Unassigned"
   * section last. Present only when at least one shipment in the block is
   * grouped; absent = render the flat `deliverables` list with no section
   * headers. Every deliverable appears in exactly one group.
   */
  groups?: MealtimeGroup[]
  /**
   * True meal count — one per bundle ordered, plus standalone main entrees.
   *
   * Computed by `shipmentsToCampuses` from the raw shipment lines and carried
   * here. NEVER derive it from `deliverables`: identical packages merge across
   * bundles and service dates, so by the time a row exists it can no longer say
   * which bundles or which days it came from. Required (not optional) so that
   * every construction site has to supply it rather than silently falling back
   * to a re-derivation — that fallback is what shipped a 205-meal lunch as 210.
   */
  meals: number
  /**
   * Distinct SERVICE days (`YYYY-MM-DD`, sorted) this block's shipments feed —
   * the "Serve Mon" / "Serve Today" badge on the mealtime heading. Usually one
   * entry; two when a same-day menu and a day-before menu share a campus +
   * mealtime on one delivery day (the deliberate food-safety split). Optional
   * because deliverables merge across days by design and some fixtures never
   * resolve it — absent renders NO badge, never a guessed one.
   */
  serveDays?: string[]
}

export interface CampusInfo {
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  zip: string
  pocName: string
  phone: string
  deliveryWindow: string
  lunchStart: string
  parking: string
  notes: string
}

export type OrderStatus = 'submitted' | 'pending' | 'missed'

export interface CampusOrderStatus {
  deadline: string
  submittedAt: string | null
}

/** A packaging/supply SKU count for a campus (trays, utensils, napkins…). */
export type { ServiceMaterialKind }

export interface SupplyLine {
  skuId: string
  name: string
  qty: number
  /** True when the SKU is its packaging group's COMPOSTABLE variant — the
   *  supplies card separates compostable from standard stock so chefs
   *  handling both never mix them. */
  isCompostable: boolean
  /** Service-material kind (utensils / plates / trays & boats / containers)
   *  — the Service materials card groups rows by it. */
  kind: ServiceMaterialKind
}

/** One mealtime's share of a milk row ("Lunch 330"), summed across service dates. */
export interface CampusMilkMealtimeQty {
  mealtime: Mealtime
  qty: number
}

/** One order group's share of a milk row (PROD-1624): `key`/`label` as on `MealtimeGroup`. */
export interface CampusMilkGroupQty {
  key: string
  label: string
  qty: number
}

/**
 * One milk item on a campus's milk card (PROD-1502). Milk leaves the mealtime
 * cards: every package whose components ALL carry a `milkOptionCode` (a milk
 * deliverable, standalone or in a bundle) and every weekly milk drop line
 * lands here, summed per milk option across mealtimes and service dates. Milk
 * inside a mixed meal package, and bagged (field-trip) or dry-run milk, stays
 * on its meal row.
 */
export interface CampusMilkLine {
  /** Canonical milk option code (legacy aliases normalized); a serving that
   *  scales the recipe (`servingMultiplier` ≠ 1) gets its own `|x<m>` row. */
  key: string
  milkOptionCode: string
  name: string
  /** Total servings (cartons, or pours from a jug) across mealtimes and days. */
  qty: number
  /** Projected component (recipe included) — feeds the print packet's
   *  ingredient list and recipe pack alongside the deliverable components. */
  component: Component
  /** Per-mealtime split, in service order; sums to `qty`. */
  byMealtime: CampusMilkMealtimeQty[]
  /**
   * Per-order-group split (PROD-1624), in section order; sums to `qty`. Set
   * only when a grouped shipment feeds this milk, so the vendor knows which
   * age group's drop-off each carton goes to; milk from an ungrouped shipment
   * on such a campus is the Unassigned share, as on the mealtime sections.
   */
  byGroup?: CampusMilkGroupQty[]
  /** Distinct SERVICE days (`YYYY-MM-DD`, sorted) this milk is for. */
  serviceDates: string[]
  temperature: Temperature
  /** Recipe serving ("6 fl oz"), when the line carries one. */
  servingSize?: string
  /** Packaging SKU name ("Gallon Jug"), when a package line names one. */
  packaging?: string
  /**
   * `Ingredient.packagingSkuKind` of the SKU `packaging` names (as on
   * `Deliverable`). BULK is the milk card's bulk pan or box, which the print
   * never reads as the milk's own package (`milkOuterPackaging`).
   */
  packagingSkuKind?: string
  /** Containers, when the milk ships in a bulk package with a spec (as `Deliverable.packing`). */
  packing?: BulkPackingPlan[]
}

export interface Campus {
  id: string
  organizationName: string
  name: string
  /** Every shipment to this destination is a chef DRY RUN — a pre-launch
   *  test cook. Drives the "do not deliver" warning on the campus card. */
  isDryRun?: boolean
  /** TASTING only when EVERY dry-run shipment here is a tasting — a mixed
   *  campus keeps the generic dry-run label. Absent means dry run. */
  dryRunServiceType?: DryRunServiceType
  info: CampusInfo
  mealtimes: MealtimeBlock[]
  /** SUPPLIES — disposables that ship with the meals (trays/boats, cutlery,
   *  plates; `ShipmentLine.isSupply`). Always empty today: nothing writes
   *  `isSupply`, so this is not waiting on template authoring but on the
   *  materializer producer parked in PR #2332. */
  supplies: SupplyLine[]
  /** PACKAGING — the containers/lids/etc. the food ships in, off the non-bulk
   *  PACKAGE lines. Categorically separate from `supplies`. */
  packaging: SupplyLine[]
  /** The milk card (PROD-1502): milk packages and weekly milk drop lines,
   *  one row per milk item, never mixed into the mealtime deliverables. The
   *  card's "Supply for X days" badge is `campusMilkSupplyDays(campus)`. */
  milk?: CampusMilkLine[]
  orderStatus: CampusOrderStatus
}

/**
 * Map a catalog `ComponentKind` (the server wire) to the chef-UI
 * `ProductType`. Pure — unit-tested. Defaults to 'main' for unknown
 * kinds so the detail panel always renders a sensible Type row.
 */
export function componentKindToProductType(kind: string | null | undefined): ProductType {
  switch (kind) {
    case 'MAIN_ENTREE':
      return 'main'
    case 'SIDE':
    case 'VEGETABLE':
    case 'FRUIT':
      return 'side'
    case 'GRAIN':
      return 'baked'
    case 'BEVERAGE':
      return 'beverage'
    case 'CONDIMENT':
    case 'SUPPLEMENT':
      return 'add-on'
    default:
      return 'main'
  }
}

const DIET_VALUES: ReadonlySet<string> = new Set<Diet>([
  'vegetarian',
  'vegan',
  'dairy-free',
  'gluten-free',
  'nut-free',
  'pork-free',
])

/** Keep only the recognized `Diet` values off a server `diets` array. Pure. */
export function toDiets(values: ReadonlyArray<string> | null | undefined): Diet[] {
  return (values ?? []).filter((v): v is Diet => DIET_VALUES.has(v))
}

export const MEALTIME_ORDER: Mealtime[] = ['breakfast', 'am-snack', 'lunch', 'pm-snack', 'supper']

export const MEALTIME_LABEL: Record<Mealtime, string> = {
  breakfast: 'Breakfast',
  'am-snack': 'AM Snack',
  lunch: 'Lunch',
  'pm-snack': 'PM Snack',
  supper: 'Supper',
}

export const PRODUCT_TYPE_LABEL: Record<ProductType, string> = {
  // 'Main', not 'Meat' — a cheese-pizza or muffin main isn't meat, and
  // unknown kinds default to 'main' (see componentKindToProductType).
  main: 'Main',
  side: 'Side',
  beverage: 'Beverage',
  dessert: 'Dessert',
  'add-on': 'Add-on',
  salad: 'Salad',
  baked: 'Baked',
  shelf: 'Shelf',
}

export const DIET_LABEL: Record<Diet, string> = {
  vegetarian: 'Vegetarian',
  vegan: 'Vegan',
  'dairy-free': 'Dairy free',
  'gluten-free': 'Gluten free',
  'nut-free': 'Nut free',
  'pork-free': 'Pork free',
}
